import { container } from 'tsyringe'
import CoinController from '@/features/experience/coin-controller'
import { ColliderActionType, MATTER_SCALE, PhysicsLayerZ, SpawnLocation } from '@/features/experience/misc/constants'
import { Color, Group, Mesh, PlaneGeometry, RawShaderMaterial, ShadowMaterial, Vector2, Vector3 } from 'three'
import studioVS from './shaders/studio-vs.glsl'
import studioFS from './shaders/studio-fs.glsl'
import RoomBase, { RoomConnectionSlot } from '../room-base'
import MajblommanWalletPhysics from './majblomman-wallet-physics'
import { Interface } from '@/shared/di/interface'
import type { IAssets, LoadingItem } from '@/shared/di/interface/assets'
import { ITouch } from '@/shared/di/interface/touch'
import WalletManager from '../wallet-manager'
import { SpawnParticleOptions } from '../../particle-system/global-particles-system'

const tempVector3 = new Vector3()
const tempVector3_2 = new Vector3()

export default class StudiotWallet extends RoomBase {
  walletManager: WalletManager

  #roomModules: RoomBase[] = []
  #exchangeSlitContainer = new Group()

  #background = new Group()
  slitRoofPosition = new Vector2(0, -this.config.roomHeight * 0.6 * 100)

  constructor() {
    super(
      {
        id: 'majblomman-wallet',
        hasExchange: true,
        hasContacts: true,
        canZoomCoin: false,
        showMenu: false,
        modelURL: '',
        cameraFov: 40,
        cameraNear: 0.1,
        cameraFar: 100,
        cameraPosition: new Vector3(0, 5.2, 20.5),
        connectedRooms: [],
        roomHeight: 17
      },
      new MajblommanWalletPhysics()
    )
    this.tubePosition = new Vector2(0, this.config.roomHeight - 1.52)

    this.walletManager = new WalletManager(this.physics, this.tubePosition)
    this.walletManager.tubeContainer.scale.setScalar(0.7)
    this.walletManager.tubeContainer.position.z = 1
    this.walletManager.onSpawnParticle.add(this.onSpawnParticle)
    this.walletManager.onCloseTube.add(() => {
      this.onTubeClosed.invoke()
    })
    this.scene.add(this.walletManager.tubeContainer)

    this.scene.add(this.#exchangeSlitContainer)
    this.#exchangeSlitContainer.position.z = PhysicsLayerZ.Front
  }

  onSpawnParticle = (options: SpawnParticleOptions) => {
    this.particleManager?.spawnParticle(options)
  }

  registerSpace(roomModule: RoomBase) {
    this.#roomModules.push(roomModule)

    this.config.connectedRooms.push({
      id: roomModule.config.id,
      slot: RoomConnectionSlot.Bottom
    })
  }

  getAdjustedManifest() {
    const manifest: LoadingItem[] = []

    return manifest
  }

  async init() {
    await super.init()

    this.light.intensity = 0
    this.light.position.set(0, 36, 10)
    this.light.target.position.set(0, 0, 2)
    this.light.lookAt(this.light.target.position)
    this.scene.add(this.light.target)
    this.light.castShadow = false
    this.light.angle = Math.PI * 0.3
    this.light.penumbra = 0.2
    this.light.shadow.mapSize.set(512, 512)
    this.light.shadow.radius = 3
    this.light.shadow.blurSamples = 8

    this.createParticleSystem()

    const assets = container.resolve<IAssets>(Interface.IAssets)

    this.scene.add(this.#background)

    const modelRoot = assets.getModel('studio')
    if (modelRoot) {
      const mesh = modelRoot.children[0].clone() as Mesh
      mesh.receiveShadow = false
      mesh.userData.enableBloom = false

      mesh.scale.setScalar(50)
      mesh.scale.x *= 2.5
      mesh.position.y = -2
      mesh.position.z = 0

      const material = new RawShaderMaterial({
        uniforms: {
          color: { value: new Color(0x94a5a2).convertSRGBToLinear() },
          colorFloor: { value: new Color(0x94a5a2).convertSRGBToLinear() }
        },
        vertexShader: studioVS,
        fragmentShader: studioFS
      })

      mesh.material = material

      this.#background.add(mesh)
    }

    /*    const shadowMesh = new Mesh(
      new PlaneGeometry(100, 200, 1, 1),
      new ShadowMaterial({ opacity: 0.3, fog: false, depthTest: true, depthWrite: true })
    )

    shadowMesh.rotation.x = Math.PI * -0.5
    shadowMesh.position.y = -1
    shadowMesh.receiveShadow = true
    shadowMesh.userData.enableBloom = false
    this.#background.add(shadowMesh)
*/
    //this.createFloorSlits()

    this.walletManager.init()
    this.walletManager.setUserName(this.userName)
  }

  activate(): void {
    super.activate()
    this.addWalletEventListeners()
    this.resizeExchangeSlits()
  }

  inactivate(): void {
    super.inactivate()
    this.removeWalletEventListeners()
  }

  activateComplete() {
    super.activateComplete()
    this.resizeExchangeSlits()
  }

  addInteractionListeners(): void {
    super.addInteractionListeners()

    const touch = container.resolve<ITouch>(Interface.ITouch)
    touch.onLongPress.add(this.onLongPress)
  }

  removeInteractionListeners(): void {
    super.removeInteractionListeners()

    const touch = container.resolve<ITouch>(Interface.ITouch)
    touch.onLongPress.remove(this.onLongPress)
  }

  onLongPress = (tap: MouseEvent) => {}

  animateCoinIntoActiveTube() {
    this.walletManager.animateCoinIn()
  }

  animateCoinFromActiveTube() {
    this.walletManager.animateCoinOut()
  }

  hideAllTubes(): Promise<void> {
    return this.walletManager.hideActiveWallet()
  }

  handleRaycastHit(triggerData: any) {
    switch (triggerData.data) {
      case ColliderActionType.COMBINE:
        //this.slitCombine?.pressedCombineButton()
        break
      /*case ColliderActionType.CLOSE_TUBE:
        this.onTubeClosed.invoke()
        this.walletManager.hideActiveWallet()
        break*/
      /*case ColliderActionType.TAP_BACKGROUND:
        this.tapBackground(triggerData.uv)
        break*/
    }
  }

  /*tapBackground(uv: Vector2) {
    if (this.physics.interactions.draggedCoins.length > 0) {
      return
    }

    this.tokenControllers.forEach(cc => {
      const x = 30 * uv.x - 15
      const y = 30 * uv.y - 15 + 8.5
      const dist = cc.mesh.position.distanceTo(tempVector3.set(x, y, 0))
      if (dist < 3) {
        cc.bounce(dist, cc.mesh.position.x < tempVector3.x ? -1 : 1)
      }
    })
  }*/

  resizeExchangeSlits = () => {
    this.cameraPivot.position.copy(this.config.cameraPosition)
  }

  private addWalletEventListeners() {}

  private removeWalletEventListeners() {}

  update(time: number, delta: number) {
    if (!this.coinZoomedIn) {
      this.walletManager.update(time, delta)

      /*if (this.walletManager.currentWalletType !== WalletType.None) {
        const distanceToTube = this.setSlitLight(null, SceneUtils.matterToSceneAtZ0(this.slitRoofPosition))
        this.walletManager.setCoinNearTube(distanceToTube)
      }*/

      if (!this.physics.interactions.isDragging && this.particleManager && this.coinContainer.children.length > 0) {
        const radius = Math.sin(time) + 2
        const x = radius * Math.cos(Math.PI * time)
        const y = Math.sin(time) * 1.2
        const z = radius * Math.sin(Math.PI * time)

        const pos = tempVector3.set(
          x + this.coinContainer.children[0].position.x,
          y + this.coinContainer.children[0].position.y,
          z
        )

        const vel = tempVector3_2.set(0, 0, 0)

        this.particleManager.spawnParticle({
          position: pos,
          velocity: vel,
          size: 3.5,
          lifetime: 1,
          velocityRandomness: 0.6,
          positionRandomness: 0.5,
          turbulence: 0.2
        })
      }
    }

    super.update(time, delta)
  }

  protected placeCoinInScene(coinController: CoinController): void {
    super.placeCoinInScene(coinController)

    coinController.useIdleRotation = true

    switch (coinController.spawnLocation) {
      case SpawnLocation.TransferParentSlit:
      case SpawnLocation.Tube:
        this.addToSpawnWaitingList(
          coinController,
          this.tubePosition.clone().multiplyScalar(-MATTER_SCALE),
          { x: 0, y: 80 },
          coinController.spawnLocation,
          0.3
        )
        break

      default:
        this.placeCoin(coinController)

        coinController.zoomIn(coinController.mesh.position)

        break
    }
  }

  resize() {
    super.resize()
    this.resizeExchangeSlits()

    //this.camera.zoom = (400 / window.innerHeight + window.innerWidth / 4000) * 1.5

    /*const cc = this.getCoinControllerByAssetId('FakeTing:1')
    if (cc) {
      if (window.innerWidth > 799 && window.innerHeight < 430) {
        this.coinContainer.position.y = 1
        this.coinContainer.position.x = -7
      } else {
        this.coinContainer.position.y = 0
        this.coinContainer.position.x = 0
      }
    }*/
  }

  destroy() {
    super.destroy()

    this.walletManager.destroy()
  }
}
